import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Radio,
  Modal,
  Form,
  DatePicker,
  Divider,
  InputNumber,
  message,
  Popconfirm,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Drawer,
  Descriptions,
  Tabs,
  Empty,
  Spin,
  Badge,
  List,
  FloatButton,
  Grid
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  PrinterOutlined,
  PayCircleOutlined,
  MoneyCollectOutlined,
  FilterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getReceipts,
  getReceiptById,
  createReceipt,
  updateReceipt,
  deleteReceipt,
  payReceipt,
  getSalesStats,
  getReceiptStatusLabel,
  getReceiptStatusColor
} from '../../services/receiptService';
import { getCustomersForDropdown } from '../../services/contactService';
import { getProducts } from '../../services/productService';
import { generateReceiptPDF } from '../../services/receiptPdfService';
import {
  getCompanySettings,
  updateCompanySettings,
  uploadCompanyLogo,
  deleteCompanyLogo,
  getLogoUrl
} from '../../services/settingsService';
import './ReceiptsPage.mobile.css';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const ReceiptsPage = () => {
  const [printType, setPrintType] = useState("full");
  const [receipts, setReceipts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [salesStats, setSalesStats] = useState({});
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();

  // Responsive state
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

  //สำหรับ modal แสดง pdf
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfPreviewReceipt, setPdfPreviewReceipt] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ข้อมูลบริษัท
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  // Pagination and filters
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    contact_id: '',
  });
  const [statusCounts, setStatusCounts] = useState([]);

  // Status options
  const statusOptions = [
    { value: 'invoice', label: 'ใบแจ้งหนี้', color: 'processing' },
    { value: 'paid', label: 'ชำระแล้ว', color: 'success' },
    { value: 'partial', label: 'ชำระบางส่วน', color: 'warning' },
    { value: 'overdue', label: 'เกินกำหนด', color: 'error' },
    { value: 'cancelled', label: 'ยกเลิก', color: 'default' },
  ];

  // Payment methods
  const paymentMethods = [
    'เงินสด',
    'โอนผ่านธนาคาร',
    'เช็ค',
    'บัตรเครดิต',
    'บัตรเดบิต',
    'อื่นๆ'
  ];


  // โหลดค่าจาก localStorage ครั้งแรกตอนเปิด Modal
  useEffect(() => {
    if (modalVisible) {
      const savedType = localStorage.getItem('PRINT_TYPE');
      if (savedType) {
        setPrintType(savedType);
      }
    }
  }, [modalVisible]);

  const handleDownloadPdf = () => {
    if (!pdfDataUrl) return;

    const link = document.createElement('a');
    link.href = pdfDataUrl;
    link.download = pdfFileName || 'receipt.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!pdfModalVisible || !pdfPreviewReceipt) {
      return;
    }

    let isActive = true;

    const refreshPdfPreview = async () => {
      setPdfLoading(true);
      setPdfDataUrl('');

      try {
        const pdfResult = await generateReceiptPDF(pdfPreviewReceipt, company || {}, printType);

        if (isActive) {
          setPdfDataUrl(pdfResult.dataUrl);
          setPdfFileName(pdfResult.fileName);
        }
      } catch (error) {
        console.error('PDF preview refresh error:', error);

        if (isActive) {
          message.error('เกิดข้อผิดพลาดในการอัปเดตตัวอย่าง PDF: ' + error.message);
        }
      } finally {
        if (isActive) {
          setPdfLoading(false);
        }
      }
    };

    refreshPdfPreview();

    return () => {
      isActive = false;
    };
  }, [pdfModalVisible, pdfPreviewReceipt, company, printType]);


  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getCompanySettings();
      //console.log('company data:', response.data.data);
      if (response.data.success) {
        const data = response.data.data;
        setCompany(data);
        form.setFieldsValue(data);
        setLogoUrl(data.logo_path ? getLogoUrl(`${data.logo_path}`) : null);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      message.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
    } finally {
      setLoading(false);
      //console.log('image url:', logoUrl);
    }
  };


  // Load data
  useEffect(() => {
    loadReceipts();
    loadCustomers();
    loadProducts();
    loadSalesStats();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const response = await getReceipts({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      });

      console.log('RECEIPT LIST:', response);

      if (response.data.success) {
        setReceipts(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total
        }));
        setStatusCounts(response.data.statusCounts || []);
      }
    } catch (error) {
      console.error('Error loading receipts:', error);
      message.error('ไม่สามารถโหลดข้อมูลใบเสร็จได้');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await getCustomersForDropdown();
      //console.log('customer list:', response);
      if (response.success) {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await getProducts({ limit: 1000 });
      //console.log('response', response)
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      //console.error('Error loading products:', error);
    }
  };

  const loadSalesStats = async () => {
    try {
      const response = await getSalesStats('month');
      if (response.data.success) {
        setSalesStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading sales stats:', error);
    }
  };

  // Modal handlers
  const showCreateModal = () => {
    setEditingReceipt(null);
    setModalVisible(true);
    form.resetFields();
    form.setFieldsValue({
      receipt_date: dayjs(),
      tax_rate: 7,
      items: [{ description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const showEditModal = (record) => {
    setEditingReceipt(record);
    setModalVisible(true);

    // Populate form with receipt data
    form.setFieldsValue({
      contact_id: record.contact_id,
      receipt_date: dayjs(record.receipt_date),
      due_date: record.due_date ? dayjs(record.due_date) : null,
      discount: record.discount,
      tax_rate: record.tax_rate,
      notes: record.notes,
      status: record.status
    });

    // Load receipt items if editing
    if (record.id) {
      loadReceiptForEdit(record.id);
    }
  };

  const loadReceiptForEdit = async (id) => {
    try {
      const response = await getReceiptById(id);
      if (response.data.success) {
        const receipt = response.data.data;
        form.setFieldsValue({
          ...form.getFieldsValue(),
          items: receipt.items.map(item => ({
            product_id: item.product_id,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price
          }))
        });
      }
    } catch (error) {
      console.error('Error loading receipt for edit:', error);
      //message.error('ไม่สามารถโหลดข้อมูลใบเสร็จได้');
    }
  };

  const showDetailDrawer = async (record) => {
    try {
      setLoading(true);
      const response = await getReceiptById(record.id);
      if (response.data.success) {
        setSelectedReceipt(response.data.data);
        setDetailDrawerVisible(true);
      }
    } catch (error) {
      console.error('Error loading receipt details:', error);
      message.error('ไม่สามารถโหลดรายละเอียดใบเสร็จได้');
    } finally {
      setLoading(false);
    }
  };

  const showPaymentModal = (record) => {
    setPaymentReceipt(record);
    setPaymentModalVisible(true);
    paymentForm.resetFields();
    paymentForm.setFieldsValue({
      paid_amount: record.total_amount - (record.paid_amount || 0)
    });
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingReceipt(null);
    form.resetFields();
  };

  const handleClosePaymentModal = () => {
    setPaymentModalVisible(false);
    setPaymentReceipt(null);
    paymentForm.resetFields();
  };

  const handleClosePdfModal = () => {
    setPdfModalVisible(false);
    setPdfDataUrl('');
    setPdfFileName('');
    setPdfPreviewReceipt(null);
    setPdfLoading(false);
  };

  // CRUD operations
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Calculate totals
      const items = values.items || [];
      const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      const discountAmount = values.discount || 0;
      const taxableAmount = subtotal - discountAmount;
      const taxAmount = (taxableAmount * (values.tax_rate || 0)) / 100;
      const totalAmount = taxableAmount + taxAmount;

      const receiptData = {
        ...values,
        receipt_date: values.receipt_date.format('YYYY-MM-DD'),
        due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
        subtotal,
        discount: discountAmount,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        items: items.map(item => ({
          barcode: item.barcode,
          product_id: item.product_id || null,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price
        }))
      };

      if (editingReceipt) {
        await updateReceipt(editingReceipt.id, receiptData);
        message.success('แก้ไขใบเสร็จเรียบร้อยแล้ว');
      } else {
        await createReceipt(receiptData);
        message.success('สร้างใบเสร็จเรียบร้อยแล้ว');
      }

      handleCloseModal();
      loadReceipts();
      loadSalesStats();
    } catch (error) {
      console.error('Error saving receipt:', error);
      message.error('เกิดข้อผิดพลาดในการบันทึกใบเสร็จ');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (values) => {
    try {
      setLoading(true);

      const data = {
        paid_amount: parseFloat(values.paid_amount),
        payment_method: values.payment_method,
        notes: values.notes
      }

      await payReceipt(paymentReceipt.id, data);
      message.success('บันทึกการชำระเงินเรียบร้อยแล้ว');
      handleClosePaymentModal();
      loadReceipts();
      loadSalesStats();

    } catch (error) {
      console.error('Error recording payment:', error);
      message.error('เกิดข้อผิดพลาดในการบันทึกการชำระเงิน');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteReceipt(id);
      message.success('ลบใบเสร็จเรียบร้อยแล้ว');
      loadReceipts();
      loadSalesStats();
    } catch (error) {
      console.error('Error deleting receipt:', error);
      message.error('เกิดข้อผิดพลาดในการลบใบเสร็จ');
    } finally {
      setLoading(false);
    }
  };

  // Mobile Receipt Card Component
  const ReceiptCard = ({ receipt }) => (
    <Card
      size="small"
      className={`mobile-receipt-card status-${receipt.status || 'unknown'}`}
    >
      <div onClick={() => showDetailDrawer(receipt)} className="mobile-receipt-card-content">
        <div className="mobile-receipt-card-head">
          <div className="mobile-receipt-document-number">
            <span>เลขที่เอกสาร</span>
            <strong>{receipt.invoice_number}</strong>
          </div>
          <Tag color={getReceiptStatusColor(receipt.status)}>
            {getReceiptStatusLabel(receipt.status)}
          </Tag>
        </div>

        {receipt.receipt_number && (
          <div className="mobile-receipt-number">
            เลขที่ใบเสร็จ: {receipt.receipt_number}
          </div>
        )}

        <div className="mobile-receipt-customer">
          <div>{receipt.contact_name || 'ไม่ระบุชื่อลูกค้า'}</div>
          {receipt.company_name && (
            <span>{receipt.company_name}</span>
          )}
        </div>

        <div className="mobile-receipt-meta">
          <span>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {dayjs(receipt.receipt_date).format('DD/MM/YYYY')}
          </span>
          {receipt.due_date && <span>ครบกำหนด {dayjs(receipt.due_date).format('DD/MM/YYYY')}</span>}
        </div>

        <div className="mobile-receipt-financials">
          <div><span>ยอดเอกสาร</span><strong>{Number(receipt.total_amount || 0).toLocaleString('th-TH',{minimumFractionDigits:2})} บาท</strong></div>
          <div><span>ชำระแล้ว</span><strong className="paid-value">{Number(receipt.paid_amount || 0).toLocaleString('th-TH',{minimumFractionDigits:2})} บาท</strong></div>
          <div><span>คงเหลือ</span><strong className="balance-value">{Math.max(0,Number(receipt.total_amount || 0)-Number(receipt.paid_amount || 0)).toLocaleString('th-TH',{minimumFractionDigits:2})} บาท</strong></div>
        </div>
      </div>
      <div className="mobile-receipt-actions">
        <Button type="text" icon={<EyeOutlined />} onClick={() => showDetailDrawer(receipt)}>ดู</Button>
        {receipt.status !== 'paid' && <Button type="text" className="payment-action" icon={<PayCircleOutlined />} onClick={() => showPaymentModal(receipt)}>รับชำระ</Button>}
        <Button type="text" className="print-action" icon={<PrinterOutlined />} onClick={() => handlePrint(receipt)}>พิมพ์</Button>
        {receipt.status !== 'paid' ? (
          <Button type="text" icon={<EditOutlined />} onClick={() => showEditModal(receipt)}>แก้ไข</Button>
        ) : (
          <Popconfirm title="คุณแน่ใจหรือไม่ที่จะลบใบเสร็จนี้?" onConfirm={() => handleDelete(receipt.id)} okText="ใช่" cancelText="ไม่">
            <Button type="text" icon={<DeleteOutlined />} danger>ลบ</Button>
          </Popconfirm>
        )}
      </div>
    </Card>
  );

  // Table columns
  const columns = [
    {
      title: 'เลขที่เอกสาร',
      key: 'document_number',
      width: 150,
      render: (text, record) => (
        <div>
          <Button
            type="link"
            onClick={() => showDetailDrawer(record)}
            style={{ padding: 0, height: 'auto' }}
          >
            {record.invoice_number}
          </Button>
          {record.receipt_number && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.receipt_number}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'ลูกค้า',
      key: 'customer',
      width: 200,
      render: (text, record) => (
        <div>
          <div>{record.contact_name}</div>
          {record.company_name && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.company_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'วันที่',
      dataIndex: 'receipt_date',
      key: 'receipt_date',
      width: 120,
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'กำหนดชำระ',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'ยอดเงิน',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (text, record) => (
        <div>
          <div>฿{parseFloat(record.total_amount).toLocaleString()}</div>
          {record.paid_amount > 0 && (
            <div style={{ fontSize: '12px', color: '#52c41a' }}>
              ชำระแล้ว ฿{parseFloat(record.paid_amount).toLocaleString()}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getReceiptStatusColor(status)}>
          {getReceiptStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'การดำเนินการ',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="ดูรายละเอียด">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => showDetailDrawer(record)}
            />
          </Tooltip>

          {record.status !== 'paid' && (
            <>
              <Tooltip title="บันทึกการชำระ">
                <Button
                  type="primary"
                  icon={<PayCircleOutlined />}
                  size="small"
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => showPaymentModal(record)}
                />
              </Tooltip>

              <Tooltip title="แก้ไข">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ backgroundColor: '#faad14', borderColor: '#faad14' }}
                  onClick={() => showEditModal(record)}
                />
              </Tooltip>
            </>
          )}

          <Tooltip title="พิมพ์">
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              size="small"
              style={{ backgroundColor: '#7e57c2', borderColor: '#7e57c2' }}
              onClick={() => handlePrint(record)}
            />
          </Tooltip>

          {record.status !== 'paid' && (
            <Popconfirm
              title="คุณแน่ใจหรือไม่ที่จะลบใบเสร็จนี้?"
              onConfirm={() => handleDelete(record.id)}
              okText="ใช่"
              cancelText="ไม่"
            >
              <Tooltip title="ลบ">
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handlePrint = async (record) => {
    try {
      message.loading('กำลังสร้าง PDF...', 1);

      // call print logic here
      console.log("Printing as:", printType);

      const response = await getReceiptById(record.id);

      if (response.data.success) {
        const receiptData = response.data.data;

        const companyInfo = {
          name: company.company_name || 'ชื่อบริษัทของคุณ',
          address: 'ที่อยู่บริษัทของคุณ',
          phone: 'หมายเลขโทรศัพท์บริษัท'
        };

        // สร้าง PDF เป็น dataUrl
        setPdfPreviewReceipt(receiptData);
        setPdfModalVisible(true);

      } else {
        //message.error('ไม่สามารถโหลดข้อมูลใบเสร็จได้');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      message.error('เกิดข้อผิดพลาดในการสร้าง PDF: ' + error.message);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleCustomerFilter = (value) => {
    setFilters(prev => ({ ...prev, contact_id: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', contact_id: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <div className={`receipts-page ${isMobile ? 'mobile-layout' : ''}`} style={{ padding: isMobile ? 0 : '24px' }}>

      {/* Modal แสดง PDF */}
      <Modal
        className={isMobile ? 'mobile-receipt-pdf-modal' : ''}
        title="ใบเสร็จ/ใบแจ้งหนี้"
        visible={pdfModalVisible}
        onCancel={handleClosePdfModal}
        footer={[
          <Button key="download" type="primary" onClick={handleDownloadPdf} disabled={!pdfDataUrl || pdfLoading}>
            ดาวน์โหลด PDF
          </Button>
        ]}
        width={isMobile ? '100%' : 800}
        bodyStyle={{ height: isMobile ? '80vh' : '600px' }}
      >
        <div style={{ marginBottom: 16 }}>
          <Radio.Group
            value={printType}
            onChange={(e) => {
              const value = e.target.value;
              setPrintType(value);
              localStorage.setItem('PRINT_TYPE', value); // ✅ บันทึกใน localStorage
            }}
          >
            <Radio value="full">แบบฟอร์มเต็ม</Radio>
            <Radio value="dot">แบบฟอร์มสำหรับเครื่อง Dot Matrix</Radio>
          </Radio.Group>
        </div>
        {pdfLoading && (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="large" />
          </div>
        )}
        {!pdfLoading && pdfDataUrl && (
          <iframe
            key={`${printType}-${pdfDataUrl.length}`}
            src={pdfDataUrl}
            style={{ width: '100%', height: '100%' }}
            frameBorder="0"
            title="ตัวอย่างเอกสาร"
          />
        )}
      </Modal>

      <div className="receipts-page-heading" style={{ marginBottom: isMobile ? 14 : 24 }}>
        <Title level={2} className="receipts-page-title">
          <FileTextOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
          {isMobile ? 'ใบเสร็จ/ใบแจ้งหนี้' : 'จัดการใบเสร็จ/ใบแจ้งหนี้'}
        </Title>
        {isMobile && <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>ออกใบเสร็จ</Button>}
      </div>

      {/* Sales Statistics */}
      <Row gutter={[12, 12]} className="receipt-stats-grid" style={{ marginBottom: isMobile ? 14 : 24 }}>
        <Col xs={12} sm={12} md={6}>
          <Card size={isMobile ? "small" : "default"} className="receipt-stat-card sales">
            <Statistic
              title="ยอดขายรวม (เดือนนี้)"
              value={salesStats.total_sales || 0}
              precision={2}
              valueStyle={{
                color: '#3f8600',
                fontSize: isMobile ? '16px' : '24px'
              }}
              prefix={<DollarOutlined />}
              suffix="บาท"
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size={isMobile ? "small" : "default"} className="receipt-stat-card documents">
            <Statistic
              title="ใบเสร็จทั้งหมด"
              value={salesStats.total_receipts || 0}
              valueStyle={{
                color: '#1890ff',
                fontSize: isMobile ? '16px' : '24px'
              }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size={isMobile ? "small" : "default"} className="receipt-stat-card paid">
            <Statistic
              title="ชำระแล้ว"
              value={salesStats.paid_receipts || 0}
              valueStyle={{
                color: '#52c41a',
                fontSize: isMobile ? '16px' : '24px'
              }}
              prefix={<MoneyCollectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size={isMobile ? "small" : "default"} className="receipt-stat-card outstanding">
            <Statistic
              title="ค้างชำระ"
              value={salesStats.outstanding_amount || 0}
              precision={2}
              valueStyle={{
                color: '#cf1322',
                fontSize: isMobile ? '16px' : '24px'
              }}
              prefix={<CalendarOutlined />}
              suffix="บาท"
            />
          </Card>
        </Col>
      </Row>

      {/* Desktop Status Filter Badges */}
      {!isMobile && (
        <Card style={{ marginBottom: '16px' }}>
          <Space size="large" wrap>
            <Badge
              count={statusCounts.find(s => s.status === 'invoice')?.count || 0}
              color="blue"
            >
              <Button
                type={filters.status === 'invoice' ? 'primary' : 'default'}
                onClick={() => handleStatusFilter(filters.status === 'invoice' ? '' : 'invoice')}
              >
                ใบแจ้งหนี้
              </Button>
            </Badge>

            <Badge
              count={statusCounts.find(s => s.status === 'paid')?.count || 0}
              color="green"
            >
              <Button
                type={filters.status === 'paid' ? 'primary' : 'default'}
                onClick={() => handleStatusFilter(filters.status === 'paid' ? '' : 'paid')}
              >
                ชำระแล้ว
              </Button>
            </Badge>

            <Badge
              count={statusCounts.find(s => s.status === 'partial')?.count || 0}
              color="orange"
            >
              <Button
                type={filters.status === 'partial' ? 'primary' : 'default'}
                onClick={() => handleStatusFilter(filters.status === 'partial' ? '' : 'partial')}
              >
                ชำระบางส่วน
              </Button>
            </Badge>

            <Badge
              count={statusCounts.find(s => s.status === 'overdue')?.count || 0}
              color="red"
            >
              <Button
                type={filters.status === 'overdue' ? 'primary' : 'default'}
                onClick={() => handleStatusFilter(filters.status === 'overdue' ? '' : 'overdue')}
              >
                เกินกำหนด
              </Button>
            </Badge>
          </Space>
        </Card>
      )}

      {/* Desktop Search and Filters */}
      {!isMobile && (
        <Card style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Input.Search
                placeholder="ค้นหาเลขที่เอกสาร, ลูกค้า"
                allowClear
                onSearch={handleSearch}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={6}>
              <Select
                placeholder="เลือกสถานะ"
                allowClear
                style={{ width: '100%' }}
                value={filters.status}
                onChange={handleStatusFilter}
              >
                {statusOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="เลือกลูกค้า"
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                value={filters.contact_id}
                onChange={handleCustomerFilter}
              >
                {customers.map(customer => (
                  <Option key={customer.id} value={customer.id}>
                    {customer.name} {customer.company_name && `(${customer.company_name})`}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Space>
                <Button onClick={clearFilters}>ล้าง</Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={showCreateModal}
                >
                  สร้างใบเสร็จ
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Mobile Search and Status Filters */}
      {isMobile && (
        <>
          <Card className="mobile-receipt-filter-card">
            <Input
              placeholder="ค้นหาเลขที่เอกสาร, ลูกค้า"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
            <div className="mobile-receipt-quick-filters">
              <Badge count={(filters.status ? 1 : 0) + (filters.contact_id ? 1 : 0)} size="small">
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setMobileFiltersVisible(true)}
                  size="small"
                >
                  ตัวกรอง
                </Button>
              </Badge>
                <Badge
                  count={statusCounts.find(s => s.status === 'invoice')?.count || 0}
                  color="blue"
                  size="small"
                >
                  <Button
                    type={filters.status === 'invoice' ? 'primary' : 'default'}
                    onClick={() => handleStatusFilter(filters.status === 'invoice' ? '' : 'invoice')}
                    size="small"
                  >
                    ใบแจ้งหนี้
                  </Button>
                </Badge>
                <Badge
                  count={statusCounts.find(s => s.status === 'paid')?.count || 0}
                  color="green"
                  size="small"
                >
                  <Button
                    type={filters.status === 'paid' ? 'primary' : 'default'}
                    onClick={() => handleStatusFilter(filters.status === 'paid' ? '' : 'paid')}
                    size="small"
                  >
                    ชำระแล้ว
                  </Button>
                </Badge>
                <Badge count={statusCounts.find(s => s.status === 'partial')?.count || 0} color="orange" size="small">
                  <Button type={filters.status === 'partial' ? 'primary' : 'default'} onClick={() => handleStatusFilter(filters.status === 'partial' ? '' : 'partial')} size="small">บางส่วน</Button>
                </Badge>
                <Badge count={statusCounts.find(s => s.status === 'overdue')?.count || 0} color="red" size="small">
                  <Button danger={filters.status !== 'overdue'} type={filters.status === 'overdue' ? 'primary' : 'default'} onClick={() => handleStatusFilter(filters.status === 'overdue' ? '' : 'overdue')} size="small">เกินกำหนด</Button>
                </Badge>
            </div>
          </Card>

          {/* Mobile Filters Drawer */}
          <Drawer
            className="mobile-receipt-filters-drawer"
            title="ตัวกรองข้อมูล"
            placement="bottom"
            height="auto"
            open={mobileFiltersVisible}
            onClose={() => setMobileFiltersVisible(false)}
          >
            <div style={{ padding: '16px 0' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>สถานะ</label>
                <Select
                  placeholder="เลือกสถานะ"
                  value={filters.status}
                  onChange={handleStatusFilter}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>ลูกค้า</label>
                <Select
                  placeholder="เลือกลูกค้า"
                  value={filters.contact_id}
                  onChange={handleCustomerFilter}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name} {customer.company_name && `(${customer.company_name})`}
                    </Option>
                  ))}
                </Select>
              </div>

              <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                <Space>
                  <Button onClick={clearFilters}>ล้างตัวกรอง</Button>
                  <Button
                    type="primary"
                    onClick={() => setMobileFiltersVisible(false)}
                  >
                    ตกลง
                  </Button>
                </Space>
              </div>
            </div>
          </Drawer>
        </>
      )}

      {/* Data Display */}
      <Card className="receipts-data-card">
        {isMobile ? (
          /* Mobile: List View */
          <List
            className="mobile-receipts-list"
            loading={loading}
            dataSource={receipts}
            renderItem={(receipt) => <ReceiptCard key={receipt.id} receipt={receipt} />}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
              },
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} จาก ${total} รายการ`,
              size: 'small'
            }}
          />
        ) : (
          /* Desktop: Table View */
          <Table
            columns={columns}
            dataSource={receipts}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} จาก ${total} รายการ`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>

      {/* Float Button for Mobile Add */}
      {isMobile && (
        <FloatButton
          icon={<PlusOutlined />}
          type="primary"
          onClick={showCreateModal}
          style={{ right: 24, bottom: 24 }}
          tooltip="สร้างใบเสร็จ"
        />
      )}

      {/* Create/Edit Receipt Form */}
      {isMobile ? (
        /* Mobile: Drawer */
        <Drawer
          className="mobile-receipt-form-drawer"
          title={editingReceipt ? 'แก้ไขใบเสร็จ' : 'สร้างใบเสร็จใหม่'}
          open={modalVisible}
          onClose={handleCloseModal}
          width="100%"
          placement="right"
          destroyOnClose
        >
          <div className="mobile-receipt-form-content">
            <ReceiptForm
              form={form}
              customers={customers}
              products={products}
              onSubmit={handleSubmit}
              onCancel={handleCloseModal}
              loading={loading}
              isEditing={!!editingReceipt}
              isMobile={isMobile}
            />
          </div>
          <div className="mobile-receipt-form-actions">
            <Button onClick={handleCloseModal}>ยกเลิก</Button>
            <Button type="primary" onClick={() => form.submit()} loading={loading}>{editingReceipt ? 'บันทึกการแก้ไข' : 'สร้างใบเสร็จ'}</Button>
          </div>
        </Drawer>
      ) : (
        /* Desktop: Modal */
        <Modal
          title={editingReceipt ? 'แก้ไขใบเสร็จ' : 'สร้างใบเสร็จใหม่'}
          open={modalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={900}
          destroyOnClose
        >
          <ReceiptForm
            form={form}
            customers={customers}
            products={products}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            loading={loading}
            isEditing={!!editingReceipt}
            isMobile={isMobile}
          />
        </Modal>
      )}

      {/* Payment Modal */}
      <Modal
        className={isMobile ? 'receipt-payment-modal' : ''}
        title="บันทึกการชำระเงิน"
        open={paymentModalVisible}
        onCancel={handleClosePaymentModal}
        footer={null}
        width={isMobile ? '100%' : 500}
      >
        <PaymentForm
          form={paymentForm}
          receipt={paymentReceipt}
          paymentMethods={paymentMethods}
          onSubmit={handlePayment}
          onCancel={handleClosePaymentModal}
          loading={loading}
          isMobile={isMobile}
        />
      </Modal>

      {/* Receipt Detail Drawer */}
      <Drawer
        className={`receipt-detail-drawer ${isMobile ? 'mobile' : ''}`}
        title="รายละเอียดใบเสร็จ"
        placement="right"
        size={isMobile ? "default" : "large"}
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
        width={isMobile ? "100%" : undefined}
      >
        {selectedReceipt && <ReceiptDetail receipt={selectedReceipt} isMobile={isMobile} />}
      </Drawer>
    </div>
  );
};

// Receipt Form Component
const ReceiptForm = ({ form, customers, products, onSubmit, onCancel, loading, isEditing, isMobile }) => {
  return (
    <Form
      className={isMobile ? 'mobile-receipt-form' : ''}
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      initialValues={{
        receipt_date: dayjs(),
        tax_rate: 7,
        discount: 0,
        items: [{ description: '', quantity: 1, unit_price: 0 }]
      }}
    >
      <Row gutter={isMobile ? [0, 16] : 16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="contact_id"
            label="ลูกค้า"
            rules={[{ required: true, message: 'กรุณาเลือกลูกค้า' }]}
          >
            <Select
              placeholder="เลือกลูกค้า"
              showSearch
              optionFilterProp="children"
            >
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.name} {customer.company_name && `(${customer.company_name})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="receipt_date"
            label="วันที่"
            rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={isMobile ? [0, 16] : 16}>
        <Col xs={24} sm={isEditing ? 12 : 24}>
          <Form.Item
            name="due_date"
            label="กำหนดชำระ"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        {isEditing && (
          <Col xs={24} sm={12}>
            <Form.Item
              name="status"
              label="สถานะ"
            >
              <Select placeholder="เลือกสถานะ">
                <Option value="invoice">ใบแจ้งหนี้</Option>
                <Option value="cancelled">ยกเลิก</Option>
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>

      <Divider>รายการสินค้า/บริการ</Divider>

      <Form.List name="items">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, itemIndex) => (
              <Card key={key} style={{ marginBottom: 14 }} size="small" className={isMobile ? 'mobile-receipt-item-card' : ''}>
                {isMobile && <div className="mobile-receipt-item-head"><strong>รายการที่ {itemIndex + 1}</strong><Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => remove(name)} disabled={fields.length === 1}>ลบรายการ</Button></div>}
                <Row gutter={isMobile ? [0, 12] : 16}>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'product_id']}
                      label="สินค้า"
                    >
                      <Select
                        placeholder="เลือกสินค้า"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        onChange={(value) => {
                          if (value) {
                            const product = products.find(p => p.id === value);
                            if (product) {
                              const items = form.getFieldValue('items');
                              items[name] = {
                                ...items[name],
                                description: product.name,
                                unit_price: product.price
                              };
                              form.setFieldsValue({ items });
                            }
                          }
                        }}
                      >
                        {products.map(product => (
                          <Option key={product.id} value={product.id}>
                            {product.name} - ฿{product.price}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={18}>
                    <Form.Item
                      {...restField}
                      name={[name, 'description']}
                      label="รายการ"
                      rules={[{ required: true, message: 'กรุณากรอกรายการ' }]}
                    >
                      <Input placeholder="รายละเอียดสินค้า/บริการ" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={isMobile ? [0, 12] : 16}>
                  <Col xs={12} sm={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'quantity']}
                      label="จำนวน"
                      rules={[{ required: true, message: 'กรุณากรอกจำนวน' }]}
                    >
                      <InputNumber
                        min={0.01}
                        step={0.01}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'unit_price']}
                      label="ราคาต่อหน่วย"
                      rules={[{ required: true, message: 'กรุณากรอกราคา' }]}
                    >
                      <InputNumber
                        min={0}
                        step={0.01}
                        style={{ width: '100%' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item label="รวม" shouldUpdate={(prevValues, currentValues) => {
                      const prevItems = prevValues.items || [];
                      const currentItems = currentValues.items || [];
                      return JSON.stringify(prevItems) !== JSON.stringify(currentItems);
                    }}>
                      {({ getFieldValue }) => {
                        const items = getFieldValue('items') || [];
                        const item = items[name] || {};
                        const total = (item.quantity || 0) * (item.unit_price || 0);
                        return (
                          <InputNumber
                            value={total}
                            disabled
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          />
                        );
                      }}
                    </Form.Item>
                  </Col>
                  {!isMobile && <Col xs={12} sm={2}>
                    <Form.Item label={isMobile ? "ลบ" : " "}>
                      <Button
                        type="text"
                        danger
                        onClick={() => remove(name)}
                        disabled={fields.length === 1}
                        style={{ width: '100%' }}
                      >
                        ลบ
                      </Button>
                    </Form.Item>
                  </Col>}
                </Row>
              </Card>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
            >
              เพิ่มรายการ
            </Button>
          </>
        )}
      </Form.List>

      <Divider>ข้อมูลเพิ่มเติม</Divider>

      <Row gutter={isMobile ? [0, 16] : 16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="discount"
            label="ส่วนลด"
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: '100%' }}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              addonAfter="บาท"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="tax_rate"
            label="อัตราภาษี (%)"
          >
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="notes"
        label="หมายเหตุ"
      >
        <TextArea rows={3} placeholder="หมายเหตุเพิ่มเติม" />
      </Form.Item>

      {!isMobile && (
        <Form.Item>
          <Space>
            <Button onClick={onCancel}>
              ยกเลิก
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              บันทึก
            </Button>
          </Space>
        </Form.Item>
      )}
    </Form>
  );
};

// Payment Form Component
const PaymentForm = ({ form, receipt, paymentMethods, onSubmit, onCancel, loading, isMobile }) => {
  if (!receipt) return null;

  const remainingAmount = receipt.total_amount - (receipt.paid_amount || 0);

  return (
    <div>
      <Descriptions column={1} bordered style={{ marginBottom: 16 }} size={isMobile ? "small" : "default"}>
        <Descriptions.Item label="เลขที่เอกสาร">
          {receipt.invoice_number}
        </Descriptions.Item>
        <Descriptions.Item label="ลูกค้า">
          {receipt.contact_name}
        </Descriptions.Item>
        <Descriptions.Item label="ยอดรวม">
          ฿{parseFloat(receipt.total_amount).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="ชำระแล้ว">
          ฿{parseFloat(receipt.paid_amount || 0).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="คงเหลือ">
          <strong style={{ color: '#cf1322' }}>
            ฿{remainingAmount.toLocaleString()}
          </strong>
        </Descriptions.Item>
      </Descriptions>

      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
      >
        <Form.Item
          name="paid_amount"
          label="จำนวนเงินที่ชำระ"
          rules={[
            { required: true, message: 'กรุณากรอกจำนวนเงิน' },
            {
              validator: (_, value) => {
                if (value > remainingAmount) {
                  return Promise.reject('จำนวนเงินที่ชำระต้องไม่เกินยอดคงเหลือ');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <InputNumber
            min={0}
            max={remainingAmount}
            step={0.01}
            style={{ width: '100%' }}
            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            addonAfter="บาท"
          />
        </Form.Item>

        <Form.Item
          name="payment_method"
          label="วิธีการชำระเงิน"
          rules={[{ required: true, message: 'กรุณาเลือกวิธีการชำระเงิน' }]}
        >
          <Select placeholder="เลือกวิธีการชำระเงิน">
            {paymentMethods.map(method => (
              <Option key={method} value={method}>
                {method}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="notes"
          label="หมายเหตุ"
        >
          <TextArea rows={3} placeholder="หมายเหตุการชำระเงิน" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={onCancel}>
              ยกเลิก
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              บันทึกการชำระ
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

// Receipt Detail Component
const ReceiptDetail = ({ receipt, isMobile }) => {
  const statusOptions = [
    { value: 'invoice', label: 'ใบแจ้งหนี้', color: 'processing' },
    { value: 'paid', label: 'ชำระแล้ว', color: 'success' },
    { value: 'partial', label: 'ชำระบางส่วน', color: 'warning' },
    { value: 'overdue', label: 'เกินกำหนด', color: 'error' },
    { value: 'cancelled', label: 'ยกเลิก', color: 'default' },
  ];

  const getStatusColor = (status) => {
    const statusConfig = statusOptions.find(opt => opt.value === status);
    return statusConfig?.color || 'default';
  };

  const getStatusLabel = (status) => {
    const statusConfig = statusOptions.find(opt => opt.value === status);
    return statusConfig?.label || status;
  };

  return (
    <Tabs defaultActiveKey="1">
      <TabPane tab="ข้อมูลใบเสร็จ" key="1">
        <Descriptions column={isMobile ? 1 : 2} bordered size={isMobile ? "small" : "default"}>
          <Descriptions.Item label="เลขที่ใบแจ้งหนี้" span={isMobile ? 1 : 2}>
            {receipt.invoice_number}
          </Descriptions.Item>
          {receipt.receipt_number && (
            <Descriptions.Item label="เลขที่ใบเสร็จ" span={isMobile ? 1 : 2}>
              {receipt.receipt_number}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="ลูกค้า" span={isMobile ? 1 : 2}>
            <div>
              <div>{receipt.contact_name}</div>
              {receipt.company_name && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {receipt.company_name}
                </div>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="วันที่">
            {dayjs(receipt.receipt_date).format('DD/MM/YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label="กำหนดชำระ">
            {receipt.due_date ? dayjs(receipt.due_date).format('DD/MM/YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="สถานะ" span={isMobile ? 1 : 2}>
            <Tag color={getStatusColor(receipt.status)}>
              {getStatusLabel(receipt.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="ยอดรวมก่อนภาษี">
            ฿{parseFloat(receipt.subtotal).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="ส่วนลด">
            ฿{parseFloat(receipt.discount).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={`ภาษี (${receipt.tax_rate || 0}%)`}>
            ฿{parseFloat(receipt.tax_amount).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="ยอดรวมทั้งสิ้น">
            <strong>฿{parseFloat(receipt.total_amount).toLocaleString()}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="ชำระแล้ว">
            <strong style={{ color: '#52c41a' }}>
              ฿{parseFloat(receipt.paid_amount || 0).toLocaleString()}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="คงเหลือ">
            <strong style={{ color: '#cf1322' }}>
              ฿{(parseFloat(receipt.total_amount) - parseFloat(receipt.paid_amount || 0)).toLocaleString()}
            </strong>
          </Descriptions.Item>
          {receipt.payment_method && (
            <Descriptions.Item label="วิธีการชำระ" span={isMobile ? 1 : 2}>
              {receipt.payment_method}
            </Descriptions.Item>
          )}
          {receipt.notes && (
            <Descriptions.Item label="หมายเหตุ" span={isMobile ? 1 : 2}>
              {receipt.notes}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="ผู้สร้าง">
            {receipt.created_by_name}
          </Descriptions.Item>
          <Descriptions.Item label="วันที่สร้าง">
            {dayjs(receipt.created_at).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
        </Descriptions>
      </TabPane>

      <TabPane tab="รายการสินค้า" key="2">
        {receipt.items && receipt.items.length > 0 ? (
          isMobile ? (
            /* Mobile: Card List */
            <List
              dataSource={receipt.items}
              renderItem={(item, index) => (
                <Card
                  size="small"
                  style={{ marginBottom: 8 }}
                  title={`รายการที่ ${index + 1}`}
                >
                  <p><strong>รายการ:</strong> {item.description}</p>
                  <Row gutter={16}>
                    <Col span={8}>
                      <p><strong>จำนวน:</strong> {item.quantity}</p>
                    </Col>
                    <Col span={8}>
                      <p><strong>ราคา/หน่วย:</strong> ฿{parseFloat(item.unit_price).toLocaleString()}</p>
                    </Col>
                    <Col span={8}>
                      <p><strong>รวม:</strong> ฿{parseFloat(item.total_price).toLocaleString()}</p>
                    </Col>
                  </Row>
                </Card>
              )}
            />
          ) : (
            /* Desktop: Table */
            <Table
              dataSource={receipt.items}
              rowKey="id"
              pagination={false}
              columns={[
                {
                  title: 'รายการ',
                  dataIndex: 'description',
                  key: 'description',
                },
                {
                  title: 'จำนวน',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  align: 'center',
                  width: 100,
                },
                {
                  title: 'ราคาต่อหน่วย',
                  dataIndex: 'unit_price',
                  key: 'unit_price',
                  align: 'right',
                  width: 120,
                  render: (price) => `฿${parseFloat(price).toLocaleString()}`,
                },
                {
                  title: 'รวม',
                  dataIndex: 'total_price',
                  key: 'total_price',
                  align: 'right',
                  width: 120,
                  render: (total) => `฿${parseFloat(total).toLocaleString()}`,
                },
              ]}
            />
          )
        ) : (
          <Empty description="ไม่มีรายการสินค้า" />
        )}
      </TabPane>
    </Tabs>
  );
};

export default ReceiptsPage;
